/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/surfaceGloss.js
 *
 * The body's surface, matte by default, its own shine and metal on a per-body
 * slider (`pose.gloss`). AnimatedResident applies it in place of forcing every
 * body to metalness 0. One implementation, shared like shoulderBand/faceRig.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then:
 *     node scripts/check_face_decal_sync.mjs --sync
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
/**
 * THE SURFACE — matte by default, the body's own shine (and metal) on a slider.
 *
 * Khari, 2026-10-31: *"I like Lyra's shiny look. For all, I would prefer we
 * default to the original matte look. For characters like Lyra, the user can
 * slide the matte---gloss value all the way up."*
 *
 * ⚠ WHY THERE IS A SHINE MAP AT ALL. Since about 2026-10-29 the rigger passes
 * through the mesh service's metallic-roughness map (it used to drop it, which
 * is why every older body is flat matte). Some of those maps are lacquer —
 * Flood of Fire (median roughness 0.20) and Avalo (0.10) looked wet — and some
 * are exactly right: Lyra's chrome is metal in hers.
 *
 * `gloss` is stored on the body (`pose.gloss`, 0..1) and read by every surface
 * that draws it, the site's viewer and the World alike:
 *   0      the original matte look: no shine or metal map, roughness 1, metal 0
 *   0..1   the map's roughness eased in (min(1, map ÷ gloss)) and its metal
 *          scaled (map × gloss)
 *   1      the map exactly as made: Lyra's chrome
 * A body with no map (every older one) looks the same at any value.
 *
 * ⚠ MATERIAL PROPERTIES ONLY, NO SHADER PATCH. The World already hangs each
 * body's lamps on its materials' uniforms (utils/residentLamps), and a second
 * `onBeforeCompile` would have to be chained through it. three.js clamps the
 * final roughness to 1 after the factor multiplies the map, so a factor of
 * 1/gloss is the whole blend.
 *
 * ⚠ SHARED VERBATIM WITH THE WORLD (HypeTunes_World/src/components/three/,
 * kept in step by its scripts/check_face_decal_sync.mjs). Edit it HERE.
 */

export const DEFAULT_GLOSS = 0

/* Each material's maps as shipped, remembered the first time it is set, so the
   slider can go back up after it has been at 0. ⚠ A WeakMap, NOT `userData`:
   three.js copies userData through JSON on `material.clone()` (the World clones
   every body's materials for its lamps), which would turn a Texture into a
   plain object. A clone is simply a new key that remembers its own maps. */
const SHIPPED = new WeakMap()

/** The stored value, clamped; anything missing or unreadable is the matte default. */
export function resolveGloss(pose) {
  const g = Number(pose?.gloss)
  return Number.isFinite(g) ? Math.min(1, Math.max(0, g)) : DEFAULT_GLOSS
}

/** One material. Remembers its shipped maps the first time, so the slider can go both ways.
    ⚠ Set it on the material that will be DRAWN: a clone made after a 0 has no maps left to remember. */
export function setMaterialGloss(m, gloss = DEFAULT_GLOSS) {
  if (!m || m.roughness === undefined) return
  if (!SHIPPED.has(m)) SHIPPED.set(m, { roughnessMap: m.roughnessMap || null, metalnessMap: m.metalnessMap || null })
  const shipped = SHIPPED.get(m)
  const g = Math.min(1, Math.max(0, Number(gloss) || 0))
  if (g <= 0.001 || (!shipped.roughnessMap && !shipped.metalnessMap)) {
    m.roughnessMap = null
    m.metalnessMap = null
    m.roughness = 1
    m.metalness = 0
  } else {
    m.roughnessMap = shipped.roughnessMap
    m.metalnessMap = shipped.metalnessMap
    m.roughness = 1 / g
    m.metalness = g
  }
  m.needsUpdate = true
}

/** Every material under `root`. */
export function applySurfaceGloss(root, gloss = DEFAULT_GLOSS) {
  root?.traverse?.((o) => {
    if (!o.isMesh && !o.isSkinnedMesh) return
    for (const m of (Array.isArray(o.material) ? o.material : [o.material])) setMaterialGloss(m, gloss)
  })
}
